/**
 * CapitalScoreBadge — compact rank pill (0–100) for non-dividend stocks.
 *
 * Drop-in replacement for `QualityBadge` on non-dividend tickers. The
 * QualityBadge pill shows a "score · tier" for dividend-payers' health
 * model; for non-div names we surface the Equity Quality Rank from the
 * 4-factor Capital Analytics engine instead. Same dot · score · short-
 * label shape so the header stays visually consistent.
 *
 * Verdict → color mapping mirrors the inside-the-tab palette:
 *   BUY   → emerald
 *   WATCH → amber
 *   AVOID → rose
 *   No rank → returns null (caller hides the slot entirely)
 *
 * Spec: /app/memory/CAPITAL_REINVEST_TAB_SPEC.md §10
 */
import { TrendingUp, Minus, TrendingDown } from 'lucide-react';

const verdictPalette = {
  BUY:   { bg: 'bg-emerald-100 dark:bg-emerald-950/60',
           text: 'text-emerald-800 dark:text-emerald-300',
           ring: 'ring-emerald-300/60 dark:ring-emerald-700/60',
           dot:  'bg-emerald-500',
           short: 'BUY',
           Icon: TrendingUp },
  WATCH: { bg: 'bg-amber-100 dark:bg-amber-950/60',
           text: 'text-amber-800 dark:text-amber-300',
           ring: 'ring-amber-300/60 dark:ring-amber-700/60',
           dot:  'bg-amber-500',
           short: 'WATCH',
           Icon: Minus },
  AVOID: { bg: 'bg-rose-100 dark:bg-rose-950/60',
           text: 'text-rose-800 dark:text-rose-300',
           ring: 'ring-rose-300/60 dark:ring-rose-700/60',
           dot:  'bg-rose-500',
           short: 'AVOID',
           Icon: TrendingDown },
};

export default function CapitalScoreBadge({
  rank,
  verdict,
  verdictLabel,
  size = 'sm',
  showLabel = false,
  onClick,
  testIdSuffix = '',
  ticker,
}) {
  if (rank === null || rank === undefined || Number.isNaN(Number(rank))) {
    return null;
  }
  const v = verdictPalette[verdict] || verdictPalette.WATCH;
  const Icon = v.Icon;

  const sizeCls = {
    xs: 'px-1 py-px text-[9px] gap-0.5',
    sm: 'px-1.5 py-0.5 text-[10px] gap-1',
    md: 'px-2 py-0.5 text-[11px] gap-1',
  }[size] || 'px-1.5 py-0.5 text-[10px] gap-1';

  const dotCls = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2 h-2',
  }[size] || 'w-2 h-2';

  const iconSize = {
    xs: 'w-2 h-2',
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
  }[size] || 'w-2.5 h-2.5';

  const tip = `Capital Analytics Rank · ${Math.round(rank)}/100 · ${verdictLabel || verdict}. Click to open the full breakdown.`;

  const pill = (
    <span
      className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full ${sizeCls} ${v.bg} ${v.text} ${v.ring} ${onClick ? 'cursor-pointer hover:ring-2 transition-all' : ''}`}
      data-testid={`capital-score-badge${testIdSuffix ? `-${testIdSuffix}` : ''}${ticker ? `-${ticker}` : ''}`}
      title={tip}
    >
      <span className={`rounded-full ${dotCls} ${v.dot}`} />
      <Icon className={iconSize} />
      {Math.round(rank)}
      <span className="opacity-70 font-mono">{v.short}</span>
      {showLabel && verdictLabel && (
        <span className="font-semibold normal-case ml-0.5 hidden sm:inline">{verdictLabel}</span>
      )}
    </span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-full"
        aria-label={`Open Capital Analytics for ${ticker}`}
        title={tip}
        data-testid={`capital-score-badge-btn${testIdSuffix ? `-${testIdSuffix}` : ''}${ticker ? `-${ticker}` : ''}`}
      >
        {pill}
      </button>
    );
  }

  return pill;
}
